import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { RichTextEditor } from '../components/RichTextEditor';
import { ImagePicker } from '../components/ImagePicker';
import type { ImageAsset } from '../components/ImagePicker';
import { downloadBlob } from '../utils/download';
import { usePdfGenerator } from '../hooks/usePdfGenerator';

const today = new Date().toISOString().split('T')[0];

export default function Home() {
  const [titlesInput, setTitlesInput] = useState('Certificate of Excellence\nCertificate of Appreciation');
  const [subtitle, setSubtitle] = useState('Crafted with Ultimate PDF Generator');
  const [selectedDate, setSelectedDate] = useState(today);
  const [metaDescription, setMetaDescription] = useState('Generate polished PDF batches instantly with UPG. Fully offline, privacy-first, and blazing fast.');
  const [bodyHtml, setBodyHtml] = useState(
    '<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque vel dolor vel ipsum ultrices consequat. Integer vitae mauris vitae nibh auctor fermentum. Suspendisse potenti.</p>' +
      '<p>Curabitur hendrerit, risus nec viverra aliquet, massa ligula pellentesque nisl, non luctus ante elit sed augue. Fusce in dictum massa, nec vehicula est.</p>',
  );
  const [imageAsset, setImageAsset] = useState<ImageAsset | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [titleFontSize, setTitleFontSize] = useState(42);
  const [isTitleBold, setIsTitleBold] = useState(true);

  const { generatePdfs, isGenerating, progress, currentTitle, error, results, hasResults, zipAll, reset } =
    usePdfGenerator();

  const titlesList = useMemo(
    () =>
      titlesInput
        .split('\n')
        .map((title) => title.trim())
        .filter((title) => title.length > 0),
    [titlesInput],
  );

  useEffect(() => {
    if (!metaDescription) return;
    const tag = document.querySelector('meta[name="description"]');
    if (tag) {
      tag.setAttribute('content', metaDescription);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = metaDescription;
      document.head.appendChild(meta);
    }
  }, [metaDescription]);

  const handleGenerate = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setFeedback(null);
      try {
        const payload = {
          subtitle,
          date: selectedDate,
          metaDescription,
          bodyHtml,
          imageDataUrl: imageAsset?.dataUrl ?? null,
          imageLink: imageAsset?.link ?? null,
          titleFontSize,
          titleIsBold: isTitleBold,
        };
        const generated = await generatePdfs(titlesList, payload);
        setFeedback(`Created ${generated.length} PDF${generated.length === 1 ? '' : 's'} successfully.`);
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : 'Unable to generate PDFs.';
        setFeedback(message);
      }
    },
    [subtitle, selectedDate, metaDescription, bodyHtml, imageAsset, titleFontSize, isTitleBold, generatePdfs, titlesList],
  );

  const handleDownloadAll = useCallback(async () => {
    const archiveName = `upg-batch-${new Date().toISOString().slice(0, 10)}.zip`;
    const { blob, filename } = await zipAll(archiveName);
    downloadBlob(blob, filename);
  }, [zipAll]);

  const handleReset = useCallback(() => {
    setTitlesInput('');
    setSubtitle('');
    setSelectedDate(today);
    setMetaDescription('');
    setBodyHtml('<p></p>');
    setImageAsset(null);
    setFeedback(null);
    setTitleFontSize(42);
    setIsTitleBold(true);
    reset();
  }, [reset]);

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 pb-16 pt-10">
      <header className="gradient-border p-[1px]">
        <div className="flex flex-col gap-6 rounded-[22px] bg-white/95 p-8 backdrop-blur">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <div>
              <p className="chip">Ultimate PDF Generator (UPG)</p>
              <h1 className="mt-3 font-display text-4xl md:text-5xl">Batch-perfect PDFs in one click.</h1>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="chip bg-emerald-100 text-emerald-600">Offline-first PWA</span>
              <span className="chip bg-indigo-100 text-indigo-600">Client-side only</span>
              <span className="chip bg-amber-100 text-amber-600">Privacy-safe</span>
            </div>
          </div>
          <p className="max-w-3xl text-lg text-slate-600">
            Enter multiple titles, set your supporting copy, and let UPG craft beautifully formatted PDFs for each
            entry. Download individually or bundle everything into a single ZIP—no server round trips, ever.
          </p>
          <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
            <span className="chip">Titles → multi-PDF batch</span>
            <span className="chip">WYSIWYG body editor</span>
            <span className="chip">High-resolution export</span>
          </div>
        </div>
      </header>

      <form className="space-y-10" onSubmit={handleGenerate}>
        <section className="section-card space-y-6">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-slate-800">Batch details</h2>
            <p className="text-sm text-slate-500">Provide one title per line. A dedicated PDF will be generated for every listed title.</p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-2">
              <label className="form-label" htmlFor="titles">
                Titles list
              </label>
              <textarea
                id="titles"
                className="input-textarea min-h-[220px] font-mono text-sm"
                placeholder={'Document One\nDocument Two\nDocument Three'}
                value={titlesInput}
                onChange={(event) => setTitlesInput(event.target.value)}
              />
              <p className="text-xs text-slate-400">Active titles: {titlesList.length || 0}</p>
              <div className="space-y-3 rounded-2xl border border-brand-100/70 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-slate-600">Title appearance</p>
                <div className="space-y-2">
                  <label className="form-label" htmlFor="title-size">
                    Font size
                  </label>
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      id="title-size"
                      type="range"
                      min={24}
                      max={72}
                      step={1}
                      value={titleFontSize}
                      onChange={(event) => setTitleFontSize(Number(event.target.value))}
                      className="h-2 w-full max-w-[220px] cursor-pointer rounded-full bg-brand-100 accent-brand-600"
                    />
                    <input
                      type="number"
                      min={12}
                      max={96}
                      value={titleFontSize}
                      onChange={(event) => {
                        const nextValue = Number(event.target.value);
                        const clamped = Number.isNaN(nextValue)
                          ? 24
                          : Math.min(Math.max(nextValue, 12), 96);
                        setTitleFontSize(clamped);
                      }}
                      className="input-base w-24"
                    />
                    <span className="chip">{titleFontSize}px</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-slate-600">Weight</span>
                  <button
                    type="button"
                    className={`secondary-button h-9 px-4 py-1 text-xs ${
                      isTitleBold ? 'border-brand-300 text-brand-600' : ''
                    }`}
                    onClick={() => setIsTitleBold((prev) => !prev)}
                  >
                    {isTitleBold ? 'Bold' : 'Regular'}
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="form-label" htmlFor="subtitle">
                  Subtitle
                </label>
                <input
                  id="subtitle"
                  className="input-base"
                  placeholder="A clean, professional statement"
                  value={subtitle}
                  onChange={(event) => setSubtitle(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="form-label" htmlFor="date">
                  Date
                </label>
                <input
                  id="date"
                  type="date"
                  className="input-base"
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="form-label" htmlFor="meta">
                  Meta description
                </label>
                <textarea
                  id="meta"
                  className="input-textarea min-h-[110px]"
                  placeholder="Short description used for SEO and PDF accessibility."
                  value={metaDescription}
                  onChange={(event) => setMetaDescription(event.target.value)}
                />
                <p className="text-xs text-slate-400">Current length: {metaDescription.length} characters</p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-card space-y-4">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-slate-800">Body content</h2>
            <p className="text-sm text-slate-500">Craft the shared narrative for your documents. Format freely or switch to HTML for fine-grained control.</p>
          </div>
          <RichTextEditor value={bodyHtml} onChange={setBodyHtml} />
        </section>

        <section className="space-y-4">
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-slate-800">Hero image</h2>
            <p className="text-sm text-slate-500">Optional imagery appears prominently in each PDF. Upload, paste, or reference an external URL.</p>
          </div>
          <ImagePicker value={imageAsset} onChange={setImageAsset} />
        </section>

        <section className="section-card space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <button type="submit" className="primary-button" disabled={isGenerating}>
              {isGenerating ? 'Generating…' : 'Generate PDFs'}
            </button>
            <button type="button" className="secondary-button" onClick={handleReset}>
              Reset form
            </button>
            {hasResults ? (
              <button type="button" className="secondary-button" onClick={handleDownloadAll}>
                Download ZIP
              </button>
            ) : null}
          </div>

          {isGenerating ? (
            <div className="rounded-2xl border border-brand-100 bg-brand-50 p-4">
              <p className="text-sm font-medium text-brand-700">Creating PDFs…</p>
              <p className="text-xs text-brand-600">
                {currentTitle ? `Processing: ${currentTitle}` : 'Preparing resources'}
              </p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-brand-100">
                <div className="h-full rounded-full bg-brand-500 transition-all" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            </div>
          ) : null}

          {feedback ? <p className="rounded-2xl bg-slate-100 px-4 py-2 text-sm text-slate-600">{feedback}</p> : null}
          {error ? <p className="rounded-2xl bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p> : null}
        </section>
      </form>

      {hasResults ? (
        <section className="space-y-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-semibold text-slate-800">Ready to download</h2>
            <p className="text-sm text-slate-500">{results.length} PDF {results.length === 1 ? 'file' : 'files'} generated. Download individually or grab the ZIP bundle.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {results.map((result) => (
              <article key={result.filename} className="section-card flex flex-col gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">{result.title}</h3>
                  <p className="text-xs text-slate-400">{result.filename}</p>
                </div>
                <button
                  type="button"
                  className="primary-button w-full"
                  onClick={() => downloadBlob(result.blob, result.filename)}
                >
                  Download PDF
                </button>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
